import React from "react";
import { Link } from "react-router-dom";

const Programs = () => {
  const programs = [
    // Rural Development
    {
      id: 1,
      title: "Sustainable Rural Development",
      category: "Core Focus",
      description:
        "Comprehensive rural development initiatives across Uttarakhand, focusing on sustainable agriculture, water conservation, and infrastructure development.",
      features: [
        "Organic farming and permaculture training",
        "Spring rejuvenation and water conservation",
        "Rural infrastructure development",
        "Community capacity building",
      ],
    },
    {
      id: 2,
      title: "Women's Empowerment",
      category: "Core Focus",
      description:
        "Empowering rural women through self-help groups, skill development, and leadership programs to enhance their socio-economic status.",
      features: [
        "Self-Help Group formation",
        "Vocational training for women",
        "Financial literacy programs",
        "Leadership development",
      ],
    },
    {
      id: 3,
      title: "Scheduled Caste/Tribe Upliftment",
      category: "Core Focus",
      description:
        "Special initiatives for the socio-economic development of scheduled castes and tribes in remote regions of Uttarakhand.",
      features: [
        "Educational support and scholarships",
        "Legal awareness and rights advocacy",
        "Livelihood generation programs",
        "Social inclusion initiatives",
      ],
    },
    {
      id: 4,
      title: "Rural Entrepreneurship",
      category: "Livelihood",
      description:
        "Fostering entrepreneurship in rural areas through training, financial linkages, and market access support.",
      features: [
        "Business development training",
        "Microfinance and credit linkages",
        "Market access and value chain development",
        "Handicraft and artisan support",
      ],
    },
    {
      id: 5,
      title: "Education & Digital Literacy",
      category: "Education",
      description:
        "Enhancing educational opportunities and digital literacy in remote areas to bridge the urban-rural divide.",
      features: [
        "Remedial education centers",
        "Digital literacy programs",
        "STEM education for girls",
        "Adult literacy initiatives",
      ],
    },
    {
      id: 6,
      title: "Health & Nutrition",
      category: "Health",
      description:
        "Improving healthcare access and nutritional standards in rural communities through innovative interventions.",
      features: [
        "Mobile health clinics",
        "Maternal and child health programs",
        "Nutrition gardens",
        "Health awareness campaigns",
      ],
    },
    {
      id: 7,
      title: "Environmental Conservation",
      category: "Environment",
      description:
        "Promoting sustainable environmental practices and conservation efforts in the Himalayan region.",
      features: [
        "Watershed management",
        "Sustainable agriculture practices",
        "Biodiversity conservation",
        "Waste management initiatives",
      ],
    },
    {
      id: 8,
      title: "Market Linkages & Value Addition",
      category: "Livelihood",
      description:
        "Creating sustainable market linkages for rural produce and promoting value addition for better income generation.",
      features: [
        "Farmer producer organizations",
        "Food processing units",
        "E-commerce platforms for rural products",
        "Branding and packaging support",
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-primary-dark/5 to-primary/10 text-center">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto animate-fade-in">
            <span className="inline-block text-accent font-semibold text-sm uppercase tracking-widest mb-4">
              Our Initiatives
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6 font-heading">
              Transforming Lives Through Action
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Our comprehensive development programs address the
              multidimensional nature of poverty, creating sustainable impact
              across Uttarakhand.
            </p>
          </div>
        </div>
      </section>

      {/* Programs Table */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="overflow-x-auto bg-white rounded-xl shadow-lg border border-gray-100">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="p-5 border-b border-primary-light font-bold whitespace-nowrap text-sm uppercase tracking-wider w-16">
                    S. No.
                  </th>
                  <th className="p-5 border-b border-primary-light font-bold whitespace-nowrap text-sm uppercase tracking-wider w-32">
                    Sector
                  </th>
                  <th className="p-5 border-b border-primary-light font-bold whitespace-nowrap text-sm uppercase tracking-wider w-48">
                    Program Name
                  </th>
                  <th className="p-5 border-b border-primary-light font-bold text-sm uppercase tracking-wider">
                    Description
                  </th>
                  <th className="p-5 border-b border-primary-light font-bold text-sm uppercase tracking-wider w-64">
                    Key Interventions
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {programs.map((program, index) => (
                  <tr
                    key={program.id}
                    className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-0"
                  >
                    <td className="p-5 align-top font-medium">{index + 1}</td>
                    <td className="p-5 align-top">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                          program.category === "primary"
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                        }`}
                      >
                        {program.category}
                      </span>
                    </td>
                    <td className="p-5 align-top font-bold text-primary">
                      {program.title}
                    </td>
                    <td className="p-5 align-top leading-relaxed">
                      {program.description}
                    </td>
                    <td className="p-5 align-top">
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        {program.features.map((feature, idx) => (
                          <li key={idx}>{feature}</li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block text-accent font-semibold text-sm uppercase tracking-widest mb-4">
              Our Valued Partners
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-heading">
              Strategic Collaborations
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full"></div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            {[
              { name: 'UNESCO', logo: '🌍' },
              { name: 'Ministry of Science and Technology', logo: '🔬' },
              { name: 'CAPART', logo: '🤝' },
              { name: 'State Rural Livelihood Mission', logo: '🏛️' },
            ].map((partner, index) => (
              <div key={index} className="text-center p-6 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors duration-300 w-full h-full flex flex-col items-center justify-center">
                <span className="text-4xl mb-3">{partner.logo}</span>
                <h3 className="text-lg font-semibold text-gray-800">{partner.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-20 bg-gradient-to-br from-primary to-accent text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { number: "100+", label: "Villages Covered" },
              { number: "50k+", label: "Beneficiaries" },
              { number: "15+", label: "Active Projects" },
              { number: "5yrs", label: "Of Impact" },
            ].map((stat, index) => (
              <div key={index} className="p-4">
                <div className="text-4xl md:text-5xl font-bold mb-2 font-heading">
                  {stat.number}
                </div>
                <div className="text-white/80 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-heading">
            Want to make a difference?
          </h2>
          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
            Your contribution can help us expand these programs to more
            communities in need.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/donate"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary to-primary-light text-white rounded-full text-lg font-bold shadow-lg hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
            >
              Donate Now
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-full text-lg font-bold hover:border-primary hover:text-primary transition-all duration-300"
            >
              Partner With Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
